import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { differenceInMinutes, isPast, parseISO } from 'date-fns';

export const useTaskReminders = (tasks, enabled = true) => {
  const checkedTasksRef = useRef(new Set());
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!enabled || !tasks || tasks.length === 0) {
      return;
    }

    // Función para verificar recordatorios
    const checkReminders = () => {
      const now = new Date();
      
      tasks.forEach(task => {
        // Solo tareas pendientes
        if (task.status !== 'pending') return;
        
        const taskDate = typeof task.start_datetime === 'string' 
          ? parseISO(task.start_datetime) 
          : new Date(task.start_datetime);
        
        // Si la tarea ya pasó, no hacer nada
        if (isPast(taskDate)) return;
        
        const minutesUntil = differenceInMinutes(taskDate, now);
        
        // Recordatorios en minutos: 1440 (1 día), 60 (1 hora), 15 (15 min)
        const reminderTimes = [1440, 60, 15];
        
        reminderTimes.forEach(reminderMinutes => {
          const reminderKey = `${task.id}-${reminderMinutes}`;
          
          // Si ya mostramos este recordatorio, no volver a mostrarlo
          if (checkedTasksRef.current.has(reminderKey)) return;
          
          // Ventana de 2 minutos para mostrar el recordatorio
          // (para evitar problemas de timing)
          if (minutesUntil <= reminderMinutes && minutesUntil >= reminderMinutes - 2) {
            checkedTasksRef.current.add(reminderKey);
            
            // Mostrar notificación
            showTaskReminder(task, reminderMinutes);
          }
        });
        
        // Recordatorio "AHORA" (la tarea está empezando)
        if (minutesUntil <= 0 && minutesUntil >= -2) {
          const nowKey = `${task.id}-now`;
          if (!checkedTasksRef.current.has(nowKey)) {
            checkedTasksRef.current.add(nowKey);
            showTaskReminderNow(task);
          }
        }
      });
    };

    // Verificar inmediatamente
    checkReminders();
    
    // Luego verificar cada minuto
    intervalRef.current = setInterval(checkReminders, 60000); // 1 minuto

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: showTaskReminder and showTaskReminderNow
  }, [tasks, enabled]);

  const showTaskReminder = (task, minutesBefore) => {
    const taskIcon = getTaskIcon(task.task_type);
    const timeText = minutesBefore === 1440 
      ? '1 día' 
      : minutesBefore === 60 
      ? '1 hora' 
      : `${minutesBefore} minutos`;

    toast.warning(
      `${taskIcon} ${task.title}`,
      {
        description: `Recordatorio: En ${timeText}`,
        duration: 10000,
        action: task.notes ? {
          label: 'Ver detalles',
          onClick: () => {
            toast.info(task.notes, {
              description: task.title,
              duration: 8000
            });
          }
        } : undefined
      }
    );

    // Intentar mostrar notificación del navegador
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`⏰ ${task.title}`, {
        body: `Recordatorio: En ${timeText}\n${task.notes || ''}`,
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: task.id,
        requireInteraction: false
      });
    }
  };

  const showTaskReminderNow = (task) => {
    const taskIcon = getTaskIcon(task.task_type);
    
    toast.error(
      `${taskIcon} ${task.title}`,
      {
        description: '🔴 ¡Es hora! La tarea debe empezar ahora',
        duration: 15000,
        action: task.notes ? {
          label: 'Ver detalles',
          onClick: () => {
            toast.info(task.notes, {
              description: task.title,
              duration: 8000
            });
          }
        } : undefined
      }
    );

    // Intentar mostrar notificación del navegador
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`🚨 ${task.title}`, {
        body: `¡Es hora! La tarea debe empezar ahora\n${task.notes || ''}`,
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: task.id,
        requireInteraction: true,
        vibrate: [200, 100, 200]
      });
    }
  };

  const getTaskIcon = (taskType) => {
    const icons = {
      'montaje': '🔧',
      'cita_profesional': '💼',
      'cita_personal': '💚',
      'ensayo': '🎵',
      'tarea_general': '✅'
    };
    return icons[taskType] || '✅';
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      console.log('Este navegador no soporta notificaciones');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  };

  return {
    requestNotificationPermission
  };
};
